import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import '../../components/DesignSystem/designSystem.css';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { useNetwork } from '../../utils/network';
import { imageAssetUrl } from '../../utils/publicAssets';
import { getYouTubeEmbedUrl } from '../../utils/youtubeEmbed';
import {
  CrystalModal,
  DesignMode,
  DesignSurface,
  SiteButton,
  SiteCountdownTimer,
  StatusPill,
  TrustBar,
  createDesignTheme,
  getDesignTokens
} from '../../components/DesignSystem';

type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  description: string;
  dotColor: string;
  imageUrl?: string | null;
  bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
};

type ApiPackage = {
  id: number;
  name: string;
  type: 'Standalone' | 'Subscription';
  description: string | null;
  imageUrl?: string | null;
  price: string | number;
  cards?: number | null;
  tokens?: number | null;
  entries?: number | null;
  Bonus?: {
    description?: string | null;
    extraCards?: number | null;
    extraEntries?: number | null;
    price?: string | number | null;
  } | null;
};

type ActiveGiveaway = {
  giveawayName: string;
  endDate: string;
  prizeAmount?: number | string | null;
};

type GiveawayStats = {
  completedGiveaways: number;
  totalGivenAwayAud: number;
  activeJackpotParticipantCount?: number;
};

type ApiWinner = {
  id?: string | number;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  discordUsername?: string | null;
  discordName?: string | null;
  giveawayName?: string | null;
  prizeAmount?: number | string | null;
  dateWon?: string | Date | null;
  endDate?: string | Date | null;
};

type WinnerItem = {
  id: string;
  name: string;
  winningDate: Date;
  prizeAmount: string;
  discordUsername?: string;
};

const JACKPOT_PRIZE_AMOUNT_AUD = 10000;
const fallbackDrawDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);
const giveawayImage = imageAssetUrl('giveaway_image_new.jpg');
const winSticker = imageAssetUrl('win_10k_sticker.png');
const nftShowcase = imageAssetUrl('nft-showcase.png');
const logoUrl =
  'https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com/logo.png';
const termsUrl =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_04_03_26.docx';
const abnLookupUrl = 'https://abr.business.gov.au/ABN/View?id=20630834762';
const rpgUtilityUrl =
  'https://store.epicgames.com/en-US/p/incrypt-shadows-of-satoshi-34602b';
const winnerVideoUrls = [
  'https://www.youtube.com/watch?v=aXb81qXMQOw',
  'https://youtu.be/QoZ8YaLrAkE'
];

const standaloneDotColours = [
  'linear-gradient(180deg,#B14CFF,#7B1BFF)',
  'linear-gradient(180deg,#7CFF3A,#3AAE12)',
  'linear-gradient(180deg,#FF4A4A,#B30000)',
  'linear-gradient(180deg,#FFD700,#C79A00)'
];

const fallbackPacks: PackItem[] = [
  {
    id: 'starter',
    title: 'Starter Pack',
    price: '9.90',
    entries: 10,
    nfts: 3,
    description: 'A simple way to enter the weekly Bitcoin draw.',
    dotColor: standaloneDotColours[0]
  },
  {
    id: 'popular',
    title: 'Popular Pack',
    price: '29.90',
    entries: 45,
    nfts: 12,
    description: 'More entries and more exclusive NFT rewards.',
    dotColor: standaloneDotColours[1]
  },
  {
    id: 'vault',
    title: 'Vault Pack',
    price: '79.90',
    entries: 140,
    nfts: 40,
    description: 'Maximum weekly entries with premium collectible rewards.',
    dotColor: standaloneDotColours[2]
  }
];

const fallbackWinners: WinnerItem[] = [
  {
    id: 'fallback-1',
    name: 'Winner',
    winningDate: new Date(),
    prizeAmount: '$10,000 in BTC'
  }
];

const heroTrustItems = [
  'Fully Compliant Giveaway',
  'Proudly Australian Owned and Operated',
  'Winners Publicly Announced',
  'NFTs Included',
  'Fair & Transparent'
];

const faqItems = [
  {
    question: 'Is this legal in Australia?',
    answer: [
      'Yes, we are an Australian registered company.',
      'ABN: 20630834762.',
      'Permit Number is TP/03891.'
    ]
  },
  {
    question: 'How does the giveaway work?',
    answer: [
      '1. Purchase your pack to automatically be in the draw.',
      '2. The winner is drawn live every Friday at 10pm in our Discord.',
      '3. The winner will be contacted via mobile and/or Discord.',
      '4. Funds will be transferred within 48 hours of the draw.'
    ]
  },
  {
    question: 'When is the draw date?',
    answer: ['The winner is drawn every Friday at 10PM AEDT.']
  },
  {
    question: 'How will I receive my Bitcoin if I win?',
    answer: [
      'The winner will be contacted via email, mobile or Discord within 24 hours of the draw. Bitcoin will be sent directly to your provided wallet address.'
    ]
  },
  {
    question: 'What are NFTs and the NFT rewards I get from a purchase?',
    answer: [
      'NFTs are collectible digital collectibles. Our NFTs can be used in our trading card game and/or our RPG game, which can be found on the Epic Games Store. They can also be traded within our marketplace in the member portal. Each NFT pack includes exclusive NFT rewards that are claimable in your member portal instantly after purchase.'
    ]
  }
];

const safeDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortPrizeLabel = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount =
    Number.isFinite(amount) && amount > 0 ? amount : JACKPOT_PRIZE_AMOUNT_AUD;

  return safeAmount.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  });
};

const formatPrizeLabel = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount =
    Number.isFinite(amount) && amount > 0 ? amount : JACKPOT_PRIZE_AMOUNT_AUD;

  return `${safeAmount.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  })} in BTC`;
};

const formatWinnerName = (winner: ApiWinner) => {
  const firstName = String(winner.firstName || '').trim();
  const lastName = String(winner.lastName || '').trim();
  const username = String(winner.username || '').trim();

  if (firstName) {
    const initial =
      lastName.length > 0
        ? lastName.replace('.', '').slice(0, 1).toUpperCase()
        : '';
    return initial ? `${firstName} ${initial}.` : firstName;
  }

  return username || 'Winner';
};

const toPackItems = (packages: ApiPackage[]): PackItem[] =>
  packages.map((pack, index) => ({
    id: String(pack.id),
    title: pack.name,
    price:
      typeof pack.price === 'number'
        ? pack.price.toFixed(2)
        : String(pack.price).replace(/^\$/, ''),
    entries: Number(pack.entries || 0),
    nfts: Number(pack.cards || 0),
    description: pack.description || '',
    imageUrl: pack.imageUrl,
    dotColor: standaloneDotColours[index % standaloneDotColours.length],
    bonus: pack.Bonus
      ? {
          description: pack.Bonus.description || '',
          extraCards: Number(pack.Bonus.extraCards || 0),
          extraEntries: Number(pack.Bonus.extraEntries || 0),
          price: Number(pack.Bonus.price || 0)
        }
      : undefined
  }));

const formatPackPrice = (price: string | number) =>
  Number(price || 0).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const scrollToId = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

const fieldSx = (mode: DesignMode) => {
  const tokens = getDesignTokens(mode);

  return {
    '& .MuiInputBase-root': {
      minHeight: 56,
      borderRadius: tokens.radius.md,
      color: tokens.colors.text,
      fontWeight: 650,
      background: mode === 'light' ? '#ffffff' : 'rgba(255,255,255,0.06)'
    },
    '& .MuiInputBase-input': {
      color: tokens.colors.text,
      WebkitTextFillColor: tokens.colors.text
    },
    '& .MuiInputLabel-root': {
      color: tokens.colors.textMuted,
      fontWeight: 650
    },
    '& fieldset': {
      borderColor: tokens.colors.border
    }
  };
};

const SectionHeader = ({
  mode,
  eyebrow,
  title,
  description,
  align = 'left',
  dark = false
}: {
  mode: DesignMode;
  eyebrow?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  dark?: boolean;
}) => {
  const tokens = getDesignTokens(mode);

  return (
    <Box
      sx={{
        maxWidth: align === 'center' ? tokens.layout.readingMax : 860,
        mx: align === 'center' ? 'auto' : 0,
        textAlign: align
      }}
    >
      {eyebrow && (
        <StatusPill mode={mode} tone={dark ? 'success' : 'info'}>
          {eyebrow}
        </StatusPill>
      )}
      <Typography
        component="h2"
        sx={{
          mt: eyebrow ? 1.1 : 0,
          color: dark ? '#f5f5f7' : tokens.colors.text,
          fontFamily: 'var(--design-font-display)',
          fontSize: { xs: 38, sm: 50, md: 68 },
          fontWeight: 800,
          letterSpacing: '-0.045em',
          lineHeight: 0.96
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            mt: 1.4,
            color: dark ? 'rgba(245,245,247,0.72)' : tokens.colors.textMuted,
            fontSize: { xs: 17, md: 21 },
            fontWeight: 500,
            lineHeight: 1.45
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const BlackCtaBand = ({
  mode,
  title,
  description,
  buttonText,
  onClick
}: {
  mode: DesignMode;
  title: string;
  description: string;
  buttonText: string;
  onClick: () => void;
}) => {
  const tokens = getDesignTokens(mode);

  return (
    <Box
      sx={{
        borderRadius: tokens.radius.lg,
        px: { xs: 2.5, md: 5 },
        py: { xs: 3, md: 4.4 },
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2.4, md: 4 },
        background:
          'linear-gradient(135deg, #07111f 0%, #0f172a 54%, #052e16 100%)',
        border: '1px solid rgba(34,197,94,0.34)',
        boxShadow: '0 22px 70px rgba(15,23,42,0.18)'
      }}
    >
      <Box>
        <Typography
          sx={{
            color: '#ffffff',
            fontFamily: 'var(--design-font-display)',
            fontSize: { xs: 34, md: 46 },
            fontWeight: 800,
            letterSpacing: '-0.035em',
            lineHeight: 1.05
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 1.2,
            color: 'rgba(255,255,255,0.72)',
            fontSize: { xs: 17, md: 21 },
            fontWeight: 650,
            lineHeight: 1.35
          }}
        >
          {description}
        </Typography>
      </Box>
      <SiteButton
        mode={mode}
        tone="success"
        size="large"
        onClick={onClick}
        sx={{
          flexShrink: 0,
          minWidth: { xs: '100%', sm: 260 },
          background: '#16a34a',
          borderColor: '#16a34a',
          boxShadow: '0 14px 34px rgba(22,163,74,0.36)',
          '&:hover': {
            background: '#15803d'
          }
        }}
      >
        {buttonText}
      </SiteButton>
    </Box>
  );
};

const DesignGiveawayPage = () => {
  const mode: DesignMode = 'light';
  const tokens = getDesignTokens(mode);
  const theme = useMemo(() => createDesignTheme(mode), [mode]);
  const { get } = useNetwork();
  const getRef = useRef(get);
  getRef.current = get;

  const [packages, setPackages] = useState<PackItem[]>([]);
  const [activeGiveaway, setActiveGiveaway] = useState<ActiveGiveaway | null>(
    null
  );
  const [giveawayStats, setGiveawayStats] = useState<GiveawayStats | null>(
    null
  );
  const [winners, setWinners] = useState<WinnerItem[]>([]);
  const [winnerVideoIndex, setWinnerVideoIndex] = useState(0);
  const [selectedPack, setSelectedPack] = useState<PackItem | null>(null);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [winnersError, setWinnersError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;

    const loadGiveawayData = async () => {
      setLoadingWinners(true);
      setWinnersError(null);

      try {
        const [packageResult, countdownResult, statsResult, winnersResult] =
          await Promise.allSettled([
            getRef.current('/package/standalone', true),
            getRef.current('/giveaway/countdown', true),
            getRef.current('/giveaway/stats', true),
            getRef.current('/giveaway/last-winners?limit=10', true)
          ]);

        if (cancelled) return;

        if (packageResult.status === 'fulfilled') {
          const packageData = Array.isArray(packageResult.value)
            ? packageResult.value
            : packageResult.value
              ? [packageResult.value]
              : [];
          setPackages(toPackItems(packageData as ApiPackage[]));
        }

        if (countdownResult.status === 'fulfilled') {
          setActiveGiveaway(countdownResult.value as ActiveGiveaway);
        }

        if (statsResult.status === 'fulfilled') {
          const data = statsResult.value as GiveawayStats;
          setGiveawayStats({
            completedGiveaways: Number(data?.completedGiveaways || 0),
            totalGivenAwayAud: Number(data?.totalGivenAwayAud || 0),
            activeJackpotParticipantCount: Number(
              data?.activeJackpotParticipantCount || 0
            )
          });
        }

        if (winnersResult.status === 'fulfilled') {
          const winnerData = Array.isArray(winnersResult.value)
            ? winnersResult.value
            : winnersResult.value
              ? [winnersResult.value]
              : [];

          const mapped = (winnerData as ApiWinner[])
            .map((winner, index) => {
              const date = safeDate(winner.dateWon ?? winner.endDate);
              if (!date) return null;

              const discord =
                (winner.discordUsername || winner.discordName || '')
                  ?.toString()
                  .trim() || undefined;

              return {
                id: String(winner.id ?? `${date.getTime()}-${index}`),
                name: formatWinnerName(winner),
                winningDate: date,
                prizeAmount: formatPrizeLabel(winner.prizeAmount),
                discordUsername: discord
              };
            })
            .filter(Boolean) as WinnerItem[];

          setWinners(mapped);
        } else {
          setWinnersError('Failed to load winners');
        }
      } catch {
        if (!cancelled) {
          setWinnersError('Failed to load winners');
        }
      } finally {
        if (!cancelled) setLoadingWinners(false);
      }
    };

    loadGiveawayData();

    return () => {
      cancelled = true;
    };
  }, []);

  const displayPacks = packages.length > 0 ? packages : fallbackPacks;
  const displayWinners = winners.length > 0 ? winners : fallbackWinners;
  const liveDrawDate = activeGiveaway?.endDate
    ? new Date(activeGiveaway.endDate)
    : fallbackDrawDate;
  const drawDate = Number.isNaN(liveDrawDate.getTime())
    ? fallbackDrawDate
    : liveDrawDate;
  const activePrizeLabel = formatShortPrizeLabel(activeGiveaway?.prizeAmount);
  const completedGiveawaysLabel = (
    giveawayStats?.completedGiveaways ?? 0
  ).toLocaleString('en-AU');
  const totalGivenAwayLabel = (
    giveawayStats?.totalGivenAwayAud ?? 0
  ).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  });
  const activeJackpotParticipantCountLabel = (
    giveawayStats?.activeJackpotParticipantCount ?? 0
  ).toLocaleString('en-AU');
  const activeVideoUrl = getYouTubeEmbedUrl(
    winnerVideoUrls[winnerVideoIndex] || winnerVideoUrls[0]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(180deg, #ffffff 0%, ${tokens.colors.page} 32%, ${tokens.colors.pageSoft} 100%)`,
          color: tokens.colors.text
        }}
      >
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            minHeight: { xs: 64, md: 76 },
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${tokens.colors.border}`,
            background: 'rgba(255,255,255,0.76)',
            backdropFilter: 'blur(22px) saturate(180%)',
            WebkitBackdropFilter: 'blur(22px) saturate(180%)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: tokens.layout.contentMax,
              mx: 'auto',
              px: tokens.spacing.pageX,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Typography
              component="a"
              href="/design"
              sx={{
                color: tokens.colors.text,
                fontFamily: 'var(--design-font-display)',
                fontSize: { xs: 22, md: 27 },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                textDecoration: 'none'
              }}
            >
              INCRYPT
            </Typography>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: { sm: 2.4, md: 3.4 }
              }}
            >
              {[
                ['Giveaway', 'design-giveaway-hero'],
                ['Winners', 'design-giveaway-winners'],
                ['Packs', 'design-giveaway-packs'],
                ['NFTs', 'design-giveaway-nfts'],
                ['FAQ', 'design-giveaway-faq']
              ].map(([label, id]) => (
                <Typography
                  key={label}
                  component="button"
                  onClick={() => scrollToId(id)}
                  sx={{
                    p: 0,
                    border: 0,
                    background: 'transparent',
                    color: tokens.colors.textMuted,
                    fontSize: { sm: 14, md: 15 },
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { color: tokens.colors.text }
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: tokens.layout.contentMax,
            mx: 'auto',
            px: tokens.spacing.pageX,
            py: { xs: 3, md: 5 },
            display: 'grid',
            gap: tokens.spacing.sectionY
          }}
        >
          <Box
            id="design-giveaway-hero"
            component="section"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '0.9fr 1.1fr' },
              gap: { xs: 3, lg: 4 },
              alignItems: 'stretch',
              scrollMarginTop: 100
            }}
          >
            <Box
              sx={{
                py: { xs: 1, lg: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                component="h1"
                sx={{
                  mt: 1.4,
                  color: tokens.colors.text,
                  fontFamily: 'var(--design-font-display)',
                  fontSize: { xs: 52, sm: 78, md: 96, lg: 104 },
                  fontWeight: 800,
                  letterSpacing: '-0.06em',
                  lineHeight: 0.9
                }}
              >
                Win {activePrizeLabel} in Bitcoin!
              </Typography>
              <Typography
                sx={{
                  mt: 2.2,
                  color: tokens.colors.textMuted,
                  fontSize: { xs: 19, md: 24 },
                  fontWeight: 500,
                  lineHeight: 1.35,
                  maxWidth: 650
                }}
              >
                Australia&apos;s most trusted weekly Bitcoin giveaway. Every
                entry comes with exclusive NFT rewards, so your next big win
                starts here.
              </Typography>

              <Box
                sx={{
                  mt: { xs: 3, md: 4 },
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.2
                }}
              >
                <SiteButton
                  mode={mode}
                  tone="success"
                  size="large"
                  startIcon={<BoltRoundedIcon />}
                  onClick={() => scrollToId('design-giveaway-packs')}
                >
                  ENTER NOW
                </SiteButton>
                <SiteButton
                  mode={mode}
                  tone="secondary"
                  size="large"
                  onClick={() => scrollToId('design-giveaway-winners')}
                >
                  View Winners
                </SiteButton>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 360, sm: 500, lg: 640 },
                borderRadius: tokens.radius.lg,
                overflow: 'hidden',
                border: `1px solid ${tokens.colors.border}`,
                background: '#ffffff',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.9), 0 30px 80px rgba(15,23,42,0.14)'
              }}
            >
              <Box
                component="img"
                src={giveawayImage}
                alt="heroImage"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  display: 'block'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.1) 54%, rgba(255,255,255,0.68) 100%)'
                }}
              />
              <Box
                component="img"
                src={winSticker}
                alt="stickerText"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: { xs: 22, sm: 26, lg: 34 },
                  transform: 'translateX(-50%)',
                  width: { xs: '92%', sm: '82%', lg: '76%' },
                  maxWidth: 780,
                  height: 'auto',
                  filter: 'drop-shadow(0 18px 28px rgba(0,0,0,0.22))'
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: tokens.spacing.cardGap,
              width: '100%'
            }}
          >
            <DesignSurface
              mode={mode}
              elevated
              sx={{ p: { xs: 2, sm: 2.8, md: 3.5 } }}
            >
              <SiteCountdownTimer
                mode={mode}
                targetDate={drawDate}
                size="large"
                label={`Next ${activePrizeLabel} Bitcoin draw closes in`}
              />
            </DesignSurface>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: { xs: 1.5, md: 2 }
              }}
            >
              {[
                [
                  activeJackpotParticipantCountLabel,
                  'Users In Upcoming $10k Giveaway'
                ],
                [completedGiveawaysLabel, 'Completed Giveaways'],
                [totalGivenAwayLabel, 'Given Away In Bitcoin']
              ].map(([value, label]) => (
                <DesignSurface
                  key={label}
                  mode={mode}
                  elevated
                  sx={{
                    minWidth: 0,
                    p: { xs: 1.5, sm: 2, md: 2.6 }
                  }}
                >
                  <Typography
                    sx={{
                      color: tokens.colors.text,
                      fontFamily: 'var(--design-font-display)',
                      fontSize: { xs: 28, sm: 36, md: 46 },
                      fontWeight: 800,
                      letterSpacing: '-0.04em',
                      lineHeight: 1
                    }}
                  >
                    {value}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.9,
                      color: tokens.colors.textMuted,
                      fontSize: { xs: 10, sm: 12, md: 14 },
                      fontWeight: 750,
                      letterSpacing: 0,
                      lineHeight: 1.35,
                      textTransform: 'uppercase'
                    }}
                  >
                    {label}
                  </Typography>
                </DesignSurface>
              ))}
            </Box>
          </Box>

          <TrustBar mode={mode} items={heroTrustItems} />

          <BlackCtaBand
            mode={mode}
            title="Ready to enter?"
            description="Secure your entries before the timer ends."
            buttonText="ENTER NOW"
            onClick={() => scrollToId('design-giveaway-packs')}
          />

          <Box
            id="design-giveaway-winners"
            component="section"
            sx={{
              display: 'grid',
              gap: tokens.spacing.sectionHeaderToContent,
              scrollMarginTop: 100
            }}
          >
            <SectionHeader
              mode={mode}
              eyebrow="Winner proof"
              title="Recent winners."
              description="Watch recent draw videos and see the latest verified giveaway winners."
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' },
                gap: tokens.spacing.cardGap,
                alignItems: 'stretch'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: tokens.radius.lg,
                  background: '#000',
                  overflow: 'hidden',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.22)'
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    pt: '56.25%'
                  }}
                >
                  {activeVideoUrl && (
                    <Box
                      component="iframe"
                      title={`Incrypt winner video ${winnerVideoIndex + 1}`}
                      src={activeVideoUrl}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                    />
                  )}
                </Box>
                {winnerVideoUrls.length > 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pointerEvents: 'none',
                      p: 1.2
                    }}
                  >
                    {[
                      ['prev', <ChevronLeftRoundedIcon key="prev" />],
                      ['next', <ChevronRightRoundedIcon key="next" />]
                    ].map(([direction, icon]) => (
                      <IconButton
                        key={String(direction)}
                        onClick={() =>
                          setWinnerVideoIndex((index) =>
                            direction === 'prev'
                              ? index === 0
                                ? winnerVideoUrls.length - 1
                                : index - 1
                              : index === winnerVideoUrls.length - 1
                                ? 0
                                : index + 1
                          )
                        }
                        sx={{
                          pointerEvents: 'auto',
                          width: 44,
                          height: 44,
                          color: '#ffffff',
                          background: 'rgba(15,23,42,0.72)',
                          border: '1px solid rgba(255,255,255,0.26)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': { background: 'rgba(15,23,42,0.9)' }
                        }}
                      >
                        {icon}
                      </IconButton>
                    ))}
                  </Box>
                )}
              </Box>

              <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  sx={{
                    color: tokens.colors.text,
                    fontFamily: 'var(--design-font-display)',
                    fontSize: { xs: 28, md: 36 },
                    fontWeight: 800,
                    letterSpacing: '-0.035em',
                    lineHeight: 1
                  }}
                >
                  Previous winners
                </Typography>
                <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                  {loadingWinners ? (
                    <Typography sx={{ color: tokens.colors.textMuted }}>
                      Loading winners…
                    </Typography>
                  ) : winnersError && winners.length === 0 ? (
                    <Typography sx={{ color: tokens.colors.red }}>
                      {winnersError}
                    </Typography>
                  ) : (
                    displayWinners.slice(0, 5).map((winner) => (
                      <Box
                        key={winner.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          py: 1.3,
                          borderBottom: `1px solid ${tokens.colors.border}`
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              color: tokens.colors.text,
                              fontSize: { xs: 16, md: 17 },
                              fontWeight: 700
                            }}
                          >
                            {winner.name}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.3,
                              color: tokens.colors.textMuted,
                              fontSize: 13,
                              fontWeight: 500
                            }}
                          >
                            {winner.winningDate.toLocaleDateString('en-AU', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: tokens.colors.text,
                            fontSize: { xs: 14, md: 15 },
                            fontWeight: 800,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {winner.prizeAmount}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </DesignSurface>
            </Box>
          </Box>

          <Box
            id="design-giveaway-packs"
            component="section"
            sx={{
              display: 'grid',
              gap: tokens.spacing.sectionHeaderToContent,
              scrollMarginTop: 100
            }}
          >
            <SectionHeader
              mode={mode}
              align="center"
              title="Choose your pack."
              description="Start your journey with INCRYPT by purchasing a one-off pack to be automatically entered into our giveaway. By purchasing any INCRYPT Package below, your name will instantly go into the draw for our weekly Bitcoin giveaway."
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: tokens.spacing.cardGap
              }}
            >
              {displayPacks.map((pack) => (
                <DesignSurface
                  key={pack.id}
                  mode={mode}
                  elevated
                  sx={{
                    p: { xs: 2.2, md: 2.8 },
                    minHeight: { xs: 500, md: 540 },
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 28px 70px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: tokens.radius.md,
                      flex: '0 0 180px',
                      border: `1px solid ${tokens.colors.border}`,
                      background: pack.imageUrl
                        ? `url(${pack.imageUrl})`
                        : `radial-gradient(circle at 72% 24%, rgba(255,255,255,0.72), transparent 18%), ${pack.dotColor}`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow:
                        'inset 0 1px 0 rgba(255,255,255,0.8), 0 14px 34px rgba(15,23,42,0.1)'
                    }}
                  />
                  <Box sx={{ pt: { xs: 2, md: 2.2 } }}>
                    <StatusPill mode={mode} tone="success">
                      + {pack.nfts} EXCLUSIVE {pack.nfts === 1 ? 'NFT' : 'NFTS'}
                    </StatusPill>
                    <Typography
                      sx={{
                        mt: 0.8,
                        color: tokens.colors.text,
                        fontFamily: 'var(--design-font-display)',
                        fontSize: { xs: 30, md: 36 },
                        fontWeight: 800,
                        letterSpacing: '-0.04em',
                        lineHeight: 1
                      }}
                    >
                      {pack.title}
                    </Typography>
                  </Box>
                  <Box sx={{ pt: 1.7 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        borderTop: `1px solid ${tokens.colors.border}`,
                        borderBottom: `1px solid ${tokens.colors.border}`,
                        py: 1.5
                      }}
                    >
                      {[
                        [
                          pack.entries.toLocaleString('en-AU'),
                          pack.entries === 1
                            ? 'Giveaway entry'
                            : 'Giveaway entries'
                        ],
                        [
                          pack.nfts.toLocaleString('en-AU'),
                          pack.nfts === 1 ? 'NFT reward' : 'NFT rewards'
                        ]
                      ].map(([value, label], index) => (
                        <Box
                          key={label}
                          sx={{
                            minWidth: 0,
                            pl: index === 1 ? 2 : 0,
                            borderLeft:
                              index === 1
                                ? `1px solid ${tokens.colors.border}`
                                : 'none'
                          }}
                        >
                          <Typography
                            sx={{
                              color: tokens.colors.green,
                              fontFamily: 'var(--design-font-display)',
                              fontSize: { xs: 24, md: 28 },
                              fontWeight: 800,
                              lineHeight: 1
                            }}
                          >
                            {value}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.45,
                              color: tokens.colors.textMuted,
                              fontSize: 13,
                              fontWeight: 700,
                              lineHeight: 1.25
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {pack.description && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          pt: 1.6
                        }}
                      >
                        <CheckRoundedIcon
                          sx={{
                            mt: 0.15,
                            flex: '0 0 auto',
                            color: tokens.colors.green,
                            fontSize: 21
                          }}
                        />
                        <Typography
                          sx={{
                            color: tokens.colors.textMuted,
                            fontSize: { xs: 14, md: 15 },
                            fontWeight: 600,
                            lineHeight: 1.42
                          }}
                        >
                          {pack.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 2.2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.4
                    }}
                  >
                    <Typography
                      sx={{
                        color: tokens.colors.text,
                        fontFamily: 'var(--design-font-display)',
                        fontSize: { xs: 34, md: 42 },
                        fontWeight: 800,
                        letterSpacing: '-0.045em',
                        lineHeight: 1
                      }}
                    >
                      {formatPackPrice(pack.price)}
                    </Typography>
                    <SiteButton
                      mode={mode}
                      tone="success"
                      size="small"
                      onClick={() => setSelectedPack(pack)}
                    >
                      Select
                    </SiteButton>
                  </Box>
                </DesignSurface>
              ))}
            </Box>
          </Box>

          <Box
            id="design-giveaway-nfts"
            component="section"
            sx={{
              display: 'grid',
              gap: tokens.spacing.sectionHeaderToContent,
              scrollMarginTop: 100
            }}
          >
            <BlackCtaBand
              mode={mode}
              title="Every entry includes NFT rewards."
              description="Pick a pack, enter the Bitcoin draw, and unlock collectibles for the Incrypt universe."
              buttonText="CHOOSE YOUR PACK"
              onClick={() => scrollToId('design-giveaway-packs')}
            />

            <Box
              sx={{
                borderRadius: tokens.radius.lg,
                border: `1px solid ${tokens.colors.border}`,
                background: '#ffffff',
                boxShadow: '0 24px 65px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                p: { xs: 1.5, md: 2.5 },
                mx: 'auto',
                width: '100%'
              }}
            >
              <Box
                component="img"
                src={nftShowcase}
                alt="Incrypt NFT Collection"
                sx={{
                  width: '100%',
                  maxHeight: { xs: 260, md: 440 },
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>

            <SectionHeader
              mode={mode}
              align="center"
              title="Our NFTs"
              description="Incrypt NFTs are more than collectibles – they are key assets in the Incrypt universe. From Trading Card Game packs to RPG gear and characters, each NFT holds unique utility. Equip, trade, and compete as you unlock their full potential across our ecosystem."
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: tokens.spacing.cardGap
              }}
            >
              {[
                [
                  <DiamondRoundedIcon key="ownership" />,
                  'True Ownership',
                  'Trade and sell your NFTs on the open marketplace',
                  undefined
                ],
                [
                  <SportsEsportsRoundedIcon key="tcg" />,
                  'Trading Card Game',
                  'Collect unique characters and build your ultimate deck',
                  undefined
                ],
                [
                  <WorkspacePremiumRoundedIcon key="rpg" />,
                  'RPG Utility',
                  'Equip gear and characters in the Incrypt RPG universe',
                  rpgUtilityUrl
                ]
              ].map(([icon, title, description, href]) => (
                <DesignSurface
                  key={String(title)}
                  mode={mode}
                  elevated
                  {...(href
                    ? {
                        component: 'a',
                        href,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                      }
                    : {})}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    minHeight: 210,
                    color: 'inherit',
                    textDecoration: 'none'
                  }}
                >
                  <Box
                    sx={{
                      color: tokens.colors.tealDeep,
                      '& svg': { fontSize: 42 }
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography
                    sx={{
                      mt: 1.4,
                      color: tokens.colors.text,
                      fontSize: { xs: 22, md: 25 },
                      fontWeight: 750,
                      lineHeight: 1.1
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      color: tokens.colors.textMuted,
                      fontSize: 16,
                      fontWeight: 500,
                      lineHeight: 1.5
                    }}
                  >
                    {description}
                  </Typography>
                </DesignSurface>
              ))}
            </Box>
          </Box>

          <Box
            id="design-giveaway-faq"
            component="section"
            sx={{
              display: 'grid',
              gap: tokens.spacing.sectionHeaderToContent,
              scrollMarginTop: 100
            }}
          >
            <SectionHeader
              mode={mode}
              align="center"
              title="Frequently Asked Questions"
              description="Everything you need to know about our Bitcoin raffle"
            />
            <Box
              sx={{
                maxWidth: 900,
                mx: 'auto',
                width: '100%',
                display: 'grid',
                gap: 1.3
              }}
            >
              {faqItems.map((item) => (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: `${tokens.radius.md * 8}px !important`,
                    border: `1px solid ${tokens.colors.border}`,
                    background: '#ffffff',
                    boxShadow: '0 12px 30px rgba(15,23,42,0.06)',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography
                      sx={{
                        color: tokens.colors.text,
                        fontSize: { xs: 16, md: 18 },
                        fontWeight: 750
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gap: 0.8 }}>
                      {item.answer.map((line) => (
                        <Typography
                          key={line}
                          sx={{
                            color: tokens.colors.textMuted,
                            fontSize: { xs: 15, md: 16 },
                            fontWeight: 500,
                            lineHeight: 1.55
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
            <DesignSurface
              mode={mode}
              elevated
              sx={{
                maxWidth: 900,
                mx: 'auto',
                width: '100%',
                p: { xs: 2.5, md: 3.5 },
                textAlign: 'center'
              }}
            >
              <Typography
                sx={{
                  color: tokens.colors.text,
                  fontSize: { xs: 20, md: 24 },
                  fontWeight: 750
                }}
              >
                Still have questions?
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: tokens.colors.textMuted,
                  fontSize: { xs: 15, md: 16 },
                  fontWeight: 500
                }}
              >
                Feel free to send us an email. Or open up a support ticket on
                our Discord.
              </Typography>
            </DesignSurface>
          </Box>

          <BlackCtaBand
            mode={mode}
            title="Ready to enter the next draw?"
            description="Choose your pack and secure your entries before the timer ends."
            buttonText="ENTER NOW"
            onClick={() => scrollToId('design-giveaway-packs')}
          />

          <Box component="footer">
            <DesignSurface
              mode={mode}
              elevated
              sx={{
                p: { xs: 2.5, md: 4 },
                display: 'grid',
                alignItems: { xs: 'start', md: 'center' },
                gridTemplateColumns: { xs: '1fr', md: '1fr 0.8fr 1.15fr' },
                gap: { xs: 2.5, md: 5 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  gap: 1.5
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Incrypt"
                  sx={{
                    height: { xs: 52, sm: 62, md: 50 },
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
                <Typography
                  sx={{
                    color: tokens.colors.textMuted,
                    fontSize: { xs: 18, sm: 20, md: 16 },
                    fontWeight: 500,
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  Copyright {new Date().getFullYear()}. Incrypt
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', md: 'column' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 2.5, md: 1 },
                  flexWrap: 'wrap'
                }}
              >
                {[
                  {
                    label: 'Terms & Conditions',
                    href: termsUrl,
                    external: true
                  },
                  {
                    label: 'Privacy Policy',
                    href: '/privacy',
                    external: false
                  }
                ].map((link) => (
                  <Typography
                    key={link.label}
                    component="a"
                    href={link.href}
                    {...(link.external
                      ? {
                          target: '_blank',
                          rel: 'noopener noreferrer'
                        }
                      : {})}
                    sx={{
                      color: tokens.colors.tealDeep,
                      fontSize: { xs: 18, md: 18 },
                      fontWeight: 650,
                      lineHeight: 1.25,
                      textDecoration: 'none',
                      '&:hover': { color: tokens.colors.teal }
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-end' },
                  textAlign: { xs: 'center', md: 'right' },
                  gap: 0.8
                }}
              >
                <Typography
                  sx={{
                    color: tokens.colors.text,
                    fontSize: { xs: 25, sm: 28, md: 22 },
                    fontWeight: 700,
                    lineHeight: 1.1
                  }}
                >
                  Customer Support
                </Typography>
                <Typography
                  component="a"
                  href="mailto:admin@incrypt.com.au"
                  sx={{
                    color: tokens.colors.tealDeep,
                    fontSize: { xs: 20, sm: 23, md: 18 },
                    fontWeight: 650,
                    lineHeight: 1.2,
                    textDecoration: 'none',
                    wordBreak: 'break-word',
                    '&:hover': { color: tokens.colors.teal }
                  }}
                >
                  admin@incrypt.com.au
                </Typography>
                <Typography
                  sx={{
                    mt: { xs: 1, md: 0.6 },
                    color: tokens.colors.textMuted,
                    fontSize: { xs: 17, sm: 18, md: 16 },
                    fontWeight: 600,
                    lineHeight: 1.3
                  }}
                >
                  INCRYPT ABN{' '}
                  <Box
                    component="a"
                    href={abnLookupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: tokens.colors.tealDeep,
                      fontWeight: 650,
                      textDecoration: 'none',
                      '&:hover': { color: tokens.colors.teal }
                    }}
                  >
                    20630834762
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: tokens.colors.textMuted,
                    fontSize: { xs: 17, sm: 18, md: 16 },
                    fontWeight: 600,
                    lineHeight: 1.3
                  }}
                >
                  Permit Number{' '}
                  <Box
                    component="span"
                    sx={{ color: tokens.colors.tealDeep, fontWeight: 650 }}
                  >
                    TP/03891
                  </Box>
                </Typography>
              </Box>
            </DesignSurface>
          </Box>
        </Box>

        <CrystalModal
          open={Boolean(selectedPack)}
          mode={mode}
          eyebrow="Entry Form"
          title={selectedPack ? `Enter with ${selectedPack.title}.` : 'Enter.'}
          description="This prototype uses the design-system modal and form styling while preserving the giveaway pack flow."
          primaryAction="TAP TO CONTINUE"
          secondaryAction="Cancel"
          onClose={() => setSelectedPack(null)}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: tokens.spacing.fieldGap
            }}
          >
            <TextField label="First name" sx={fieldSx(mode)} />
            <TextField label="Last name" sx={fieldSx(mode)} />
            <TextField label="Username" sx={fieldSx(mode)} />
            <TextField label="Email" sx={fieldSx(mode)} />
            <TextField
              select
              label="Country"
              defaultValue="Australia"
              sx={fieldSx(mode)}
            >
              <MenuItem value="Australia">Australia</MenuItem>
              <MenuItem value="New Zealand">New Zealand</MenuItem>
            </TextField>
            <TextField label="Phone" sx={fieldSx(mode)} />
          </Box>
          {selectedPack && (
            <DesignSurface mode={mode} sx={{ mt: 2, p: 1.8 }}>
              <Typography
                sx={{
                  color: tokens.colors.text,
                  fontSize: 17,
                  fontWeight: 750
                }}
              >
                {selectedPack.title} · {formatPackPrice(selectedPack.price)}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: tokens.colors.textMuted,
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                {selectedPack.entries.toLocaleString('en-AU')} entries ·{' '}
                {selectedPack.nfts.toLocaleString('en-AU')} NFT rewards
              </Typography>
            </DesignSurface>
          )}
          <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
            <FormControlLabel
              control={<Checkbox defaultChecked />}
              label="I agree to the giveaway terms and conditions."
              sx={{
                color: tokens.colors.text,
                '& .MuiFormControlLabel-label': { fontWeight: 650 }
              }}
            />
            <FormControlLabel
              control={<Checkbox defaultChecked />}
              label="I am eligible to enter this giveaway."
              sx={{
                color: tokens.colors.text,
                '& .MuiFormControlLabel-label': { fontWeight: 650 }
              }}
            />
          </Box>
        </CrystalModal>
      </Box>
    </ThemeProvider>
  );
};

export default DesignGiveawayPage;
